import React, {useCallback, useEffect, useState} from 'react';
import styles from '../AdminPortal.module.css';
import {PRISMAX_DATA_PIPELINE_URL} from '../../../configs/config';
import {getApiErrorMessage} from '../../../utils/authErrors';

const EMPTY_ACTIVE = {
  has_active_flow: false,
  lookback_hours: 24,
  upload_count: 0,
  episode_count: 0,
  has_worker_activity: false,
  worker_deriving_count: 0,
  uploads_by_status: {},
  episodes_by_status: {},
  oldest_at: null,
  latest_at: null,
};

function formatCounts(counts) {
  const entries = Object.entries(counts || {}).filter(([, count]) => Number(count) > 0);
  if (!entries.length) return 'none';
  return entries.map(([status, count]) => `${status}: ${count}`).join(', ');
}

function formatDateTime(value) {
  if (!value) return 'created time unavailable';
  const normalized = String(value).endsWith('+00:00Z')
    ? String(value).slice(0, -1)
    : value;
  const date = new Date(normalized);
  if (!Number.isFinite(date.getTime())) return 'created time unavailable';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function compactValue(label, value) {
  if (value === null || value === undefined || value === '') return null;
  return `${label} ${value}`;
}

function formatUploadSample(sample) {
  const pieces = [
    `Upload #${sample.upload_id}`,
    sample.status,
    formatDateTime(sample.created_at),
    compactValue('task', sample.task_id),
    compactValue('machine', sample.machine_id),
  ].filter(Boolean);
  return pieces.join(' · ');
}

function formatEpisodeSample(sample) {
  const pieces = [
    `Episode #${sample.episode_id}`,
    compactValue('upload', sample.upload_id),
    sample.status,
    formatDateTime(sample.activity_at || sample.created_at || sample.upload_created_at),
    compactValue('task', sample.task_id),
  ].filter(Boolean);
  return pieces.join(' · ');
}

const DataUploadPauseControl = ({adminToken: adminTokenProp = ''}) => {
  const adminToken = adminTokenProp || localStorage.getItem('prismax_admin_token') || '';
  const [control, setControl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const recentActive = control?.recent_active || control?.active || EMPTY_ACTIVE;
  const olderStuck = control?.older_stuck || EMPTY_ACTIVE;

  const loadControl = useCallback(async () => {
    if (!adminToken) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/admin/upload-control`, {
        headers: {Authorization: `Bearer ${adminToken}`},
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.success) {
        throw new Error(getApiErrorMessage(payload, 'Failed to load upload control.'));
      }
      setControl(payload.data || {});
    } catch (err) {
      setError(err.message || 'Failed to load upload control.');
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    void loadControl();
  }, [loadControl]);

  const updatePaused = async (paused) => {
    if (!adminToken || saving) return;
    setSaving(true);
    setError('');
    try {
      const response = await fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/admin/upload-control`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${adminToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({paused}),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.success) {
        if (payload?.data) {
          setControl(payload.data);
        }
        throw new Error(getApiErrorMessage(payload, 'Failed to update upload control.'));
      }
      setControl(payload.data || {});
    } catch (err) {
      setError(err.message || 'Failed to update upload control.');
    } finally {
      setSaving(false);
    }
  };

  if (!adminToken) return null;

  const paused = Boolean(control?.paused);
  const recentActiveCount = Number(recentActive.upload_count || 0) + Number(recentActive.episode_count || 0);
  const olderStuckCount = Number(olderStuck.upload_count || 0) + Number(olderStuck.episode_count || 0);
  const lookbackHours = Number(recentActive.lookback_hours || 24);
  const recentUploadSamples = (recentActive.upload_samples || []).slice(0, 5);
  const recentEpisodeSamples = (recentActive.episode_samples || []).slice(0, 5);
  const fallbackDerivingCount = Number(recentActive?.uploads_by_status?.DERIVING || 0) + Number(recentActive?.episodes_by_status?.DERIVING || 0);
  const derivingCount = Number(recentActive?.worker_deriving_count ?? fallbackDerivingCount);
  const uploadedPendingCount = Number(recentActive?.uploads_by_status?.UPLOADED || 0) + Number(recentActive?.episodes_by_status?.UPLOADED || 0);
  const workerStatusText = derivingCount > 0
    ? `DERIVING: ${derivingCount}`
    : 'No active manifest worker';
  const workerStatusDetail = derivingCount > 0
    ? 'Manifest worker is processing uploaded episodes. Pause is disabled until this finishes.'
    : uploadedPendingCount > 0
      ? `No worker is deriving right now. ${uploadedPendingCount} uploaded record${uploadedPendingCount === 1 ? '' : 's'} may be waiting or stuck.`
      : 'No active manifest worker activity detected.';
  const pauseDisabled = loading || saving || !control || (!paused && derivingCount > 0);

  return (
    <section className={styles.uploadControlCard}>
      <div className={styles.uploadControlHeader}>
        <div>
          <h2>Data upload control</h2>
          <p>Pause the upload UI and new upload sessions during maintenance.</p>
        </div>
        <span className={paused ? styles.uploadControlPaused : styles.uploadControlEnabled}>
          {paused ? 'Uploads paused' : 'Uploads enabled'}
        </span>
      </div>

      <div className={styles.uploadControlBody}>
        <div>
          <span className={styles.uploadControlLabel}>Recent uploads, last {lookbackHours}h</span>
          <strong>{formatCounts(recentActive.uploads_by_status)}</strong>
        </div>
        <div>
          <span className={styles.uploadControlLabel}>Recent worker episodes</span>
          <strong>{formatCounts(recentActive.episodes_by_status)}</strong>
        </div>
      </div>

      <div className={styles.uploadControlWorkerPanel}>
        <span className={styles.uploadControlLabel}>Manifest worker status</span>
        <strong>{workerStatusText}</strong>
        <p>{workerStatusDetail}</p>
      </div>

      {recentActiveCount > 0 && (
        <div className={styles.uploadControlRecentDetails}>
          <div>
            <span className={styles.uploadControlLabel}>Recent upload IDs</span>
            {recentUploadSamples.length ? (
              recentUploadSamples.map((sample) => (
                <p key={`upload-${sample.upload_id}`}>{formatUploadSample(sample)}</p>
              ))
            ) : (
              <p>none</p>
            )}
          </div>
          <div>
            <span className={styles.uploadControlLabel}>Recent worker episode IDs</span>
            {recentEpisodeSamples.length ? (
              recentEpisodeSamples.map((sample) => (
                <p key={`episode-${sample.episode_id}`}>{formatEpisodeSample(sample)}</p>
              ))
            ) : (
              <p>none</p>
            )}
          </div>
        </div>
      )}

      {olderStuckCount > 0 && (
        <div className={styles.uploadControlStuck}>
          <strong>Older stuck records:</strong>{' '}
          uploads {formatCounts(olderStuck.uploads_by_status)} · episodes {formatCounts(olderStuck.episodes_by_status)}
          <span>
            Oldest {formatDateTime(olderStuck.oldest_at)} · Latest {formatDateTime(olderStuck.latest_at)}
          </span>
        </div>
      )}

      {recentActiveCount > 0 && (
        <div className={styles.uploadControlWarning}>
          Recent upload records were detected. Review them before pausing uploads.
        </div>
      )}
      {derivingCount > 0 && (
        <div className={styles.uploadControlWarning}>
          Worker is deriving {derivingCount} episode{derivingCount === 1 ? '' : 's'}. Pause uploads is disabled until deriving finishes.
        </div>
      )}
      {error && <div className={styles.uploadControlError}>{error}</div>}

      <div className={styles.uploadControlActions}>
        <button
          type="button"
          className={styles.uploadControlSecondaryButton}
          onClick={loadControl}
          disabled={loading || saving}
        >
          {loading ? 'Checking...' : 'Check status'}
        </button>
        <button
          type="button"
          className={paused ? styles.uploadControlPrimaryButton : styles.uploadControlDangerButton}
          onClick={() => updatePaused(!paused)}
          disabled={pauseDisabled}
        >
          {saving ? 'Saving...' : (paused ? 'Resume uploads' : 'Pause uploads')}
        </button>
      </div>
    </section>
  );
};

export default DataUploadPauseControl;
